import React, { useState } from 'react';
import { Layout, Select, Input, Button, Typography, Tag, Flex, theme } from 'antd';
import {
  FileTextOutlined,
  CodeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import HeaderApp from '../../shared/components/HeaderApp';
import FileDragger from '../../shared/components/FileDragger';
import type { BillJson, InvoiceType, ParamsSearchStatusBill } from '../types';
import type { UploadProps } from "antd";
import { XMLParser } from "fast-xml-parser";
import BillDetails from './billDetails';
import { statusBillColors, type StatusKeyBill } from '../../../constans/statusBill';
import { useFetchStatusBill } from '../hooks/useFetchStatusBill';
import { uploadFile } from '../../../lib/files/api/fetchFile';
import openNotification from '../../../utils/openNotification';
import { useCreateBill } from '../hooks/userCreateBill';
import Loading from '../../shared/components/Loading';
import { parseJsonBill } from '../utils/parseJsonBill';
import { URL_FILES } from '../../../constans/apis';

const { Option } = Select;
const { TextArea } = Input;

const NewBill: React.FC = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [invoiceType, setInvoiceType] = useState<InvoiceType | ''>('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [billJson, setBillJson] = useState<BillJson | null>(null);
  const [isLoadingCreate, setIsloadingcreate] = useState(false);


  const { mutate, data: dataStatus, isPending } = useFetchStatusBill();
  const { mutate: createBillMutation, isPending: isLoading } = useCreateBill();

  const handleSubmit = async () => {
    let uuid_xml = undefined;
    let uuid_pdf = undefined;

    if (!invoiceType) {
      openNotification('info', '', 'Selecionar el tipo de factura');
      return;
    }

    if (!billJson) {
      openNotification('error', 'Error', 'Debes cargar un XML primero');
      return;
    }
    setIsloadingcreate(true);

    // Upload xml file
    if (xmlFile) {
      const resFile = await uploadFile(xmlFile, URL_FILES, TOKEN_FILES);
      if (resFile.status != 201) {
        openNotification('error', resFile.message);
        setIsloadingcreate(false);

        return;
      }
      uuid_xml = resFile.data?.UUID_file;
    }

    // upload pdf file
    if (pdfFile) {
      const resFile = await uploadFile(pdfFile, url_files, TOKEN_FILES);
      if (resFile.status != 201) {
        openNotification('error', resFile.message);
        setIsloadingcreate(false);
        return;
      }
      uuid_pdf = resFile.data?.UUID_file;
    }


    const payload = {
      ...billJson,
      uuid_pdf: uuid_pdf,
      uuid_xml: uuid_xml,
      notes,
      invoiceType
    };
    if (dataStatus?.data?.Estado) {
      payload.status_bill = dataStatus?.data?.Estado;
    }

    createBillMutation(payload, {
      onSuccess: () => {
        // Redirect to facturas list
        navigate('/facturas');
        setIsloadingcreate(false);

      },
      onError: (error) => {
        setIsloadingcreate(false);
        openNotification('error', `Error`, error.message);
      }
    })
  };

  const handleFileChange: UploadProps["beforeUpload"] = async (file) => {
    setXmlFile(file);
    try {
      const text = await file.text();

      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: "",
        isArray: (tagName) => {
          return tagName === "cfdi:Concepto";
        },
      });

      const json = parser.parse(text);

      const jsonBill = json['cfdi:Comprobante'];
      const completed = jsonBill['cfdi:Complemento'];
      const hasUUID = completed['tfd:TimbreFiscalDigital'].UUID;
      if (hasUUID) {
        getStatusBill({
          rfcSender: jsonBill['cfdi:Emisor'].Rfc,
          rfcRecipient: jsonBill['cfdi:Receptor'].Rfc,
          total: jsonBill.Total,
          uuidBill: completed['tfd:TimbreFiscalDigital'].UUID
        });
      }
      setBillJson(parseJsonBill(jsonBill));

      // message.success("XML parsed successfully");

    } catch (error) {
      // message.error("Invalid XML file");
    }

    return false; // 🔥 evita auto upload
  };



  const getStatusBill = (paramsGetStatus: ParamsSearchStatusBill) => {
    mutate(paramsGetStatus)
  }

  const getStatusIcon = (status: StatusKeyBill) => {
    switch (status) {
      case "Vigente":
        return <CheckCircleOutlined />;
      case "Cancelado":
        return <CloseCircleOutlined />;
      case "En Proceso":
        return <ClockCircleOutlined />;
    }
  };

  if (isLoading || isLoadingCreate) {
    return <Loading />
  }
  return (
    <>
      <HeaderApp
        sectionName="Subir Factura"
        showBack
        backPath="/facturas"
      />

      <Layout.Content style={{ padding: '16px', paddingBottom: '140px' }}>
        {/* Description */}
        <div style={{ marginBottom: '24px' }}>
          <TextArea
            value="Carga los documentos necesarios para registrar tu nueva factura en el sistema."
            readOnly
            bordered={false}
            style={{
              color: token.colorTextSecondary,
              fontSize: '14px',
              padding: 0,
              background: 'transparent'
            }}
          />
        </div>

        {/* Invoice Type Select */}
        <div style={{ marginBottom: '24px' }}>
          <Typography.Text
            strong
            style={{
              display: 'block',
              marginBottom: '8px',
              color: token.colorText,
              fontSize: '14px'
            }}
          >
            Tipo de Factura
          </Typography.Text>
          <Select
            value={invoiceType}
            onChange={setInvoiceType}
            placeholder="Selecciona una opción"
            style={{ width: '100%' }}
            size="large"
          >
            <Option value="ingreso">Ingreso</Option>
            <Option value="egreso">Egreso</Option>
            <Option value="traslado">Traslado</Option>
            <Option value="nomina">Nómina</Option>
            <Option value="pago">Pago</Option>
          </Select>
        </div>

        {/* PDF Dragger */}
        <FileDragger
          label="Archivo PDF"
          description="Formato .pdf (Máx. 5MB)"
          acceptedTypes=".pdf"
          maxSizeMB={5}
          icon={<FileTextOutlined style={{ fontSize: '36px' }} />}
          iconBgColor="rgba(239, 68, 68, 0.1)"
          iconColor={token.colorError}
          value={pdfFile}
          onChange={(file) => setPdfFile(file as File | null)}
        />

        {/* XML Dragger */}
        <FileDragger
          label="Archivo XML"
          description="Formato .xml (Requerido)"
          acceptedTypes=".xml"
          maxSizeMB={5}
          required
          icon={<CodeOutlined style={{ fontSize: '36px' }} />}
          iconBgColor="rgba(59, 130, 246, 0.1)"
          iconColor={token.colorPrimary}
          value={xmlFile}
          // onChange={(file) => setXmlFile(file as File | null)}
          onChange={handleFileChange}
        />

        {/* Notes */}
        <div style={{ marginBottom: '24px' }}>
          <Typography.Text
            strong
            style={{
              display: 'block',
              marginBottom: '8px',
              color: token.colorText,
              fontSize: '14px'
            }}
          >
            Notas adicionales
          </Typography.Text>
          <TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Escribe aquí cualquier detalle, referencia o comentario sobre esta factura..."
            rows={4}
            style={{
              borderRadius: '12px',
              backgroundColor: token.colorBgContainer,
              borderColor: token.colorBorder,
            }}
          />
        </div>

        <Flex align="center" gap={8} className="mb-30">
          <Typography.Title level={4} style={{ margin: 0 }}>
            Estatus factura:
          </Typography.Title>

          {isPending ? (
            <Loading />
          ) : (
            <Tag
              icon={getStatusIcon(dataStatus?.data?.Estado)}
              color={statusBillColors[dataStatus?.data?.Estado]?.color || "default"}
              style={{
                fontSize: 15,
                padding: "6px 16px",
                fontWeight: 600,
                borderRadius: 8,
              }}
            >
              {dataStatus?.data?.Estado || "Sin información"}
            </Tag>
          )}
        </Flex>

        <BillDetails billJson={billJson} />

      </Layout.Content>

      {/* Fixed Bottom Button */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        maxWidth: '28rem',
        margin: '0 auto',
        padding: '16px',
        background: (token as any).customBgBlur || 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(12px)',
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        zIndex: 50,
      }}>
        <Button
          type="primary"
          onClick={handleSubmit}
          icon={<CheckCircleOutlined />}
          style={{
            width: '100%',
            height: '48px',
            fontSize: '16px',
            fontWeight: 'bold',
            borderRadius: '12px',
            background: token.colorPrimary,
            border: 'none',
            boxShadow: '0 4px 6px -1px rgba(43, 140, 238, 0.3)',
          }}
        >
          Subir Factura
        </Button>
      </div>
    </>

  );
};

export default NewBill;
